/**
 * @mention suggestions for the comment composer.
 *
 * A mention is plain text inside the comment body; the API carries no mention
 * payload. Identity is recovered at render time by matching the written label
 * against the people known for that thread (`buildMentionIndex`), so the
 * composer only ever suggests those same people — anyone else would be written
 * as a label that resolves to nobody once the thread is refetched.
 *
 * For the same reason the label written into the text is chosen here rather
 * than at the call site: it has to be one the renderer can resolve back to a
 * single person.
 */
import { getFirstName } from './postDisplay';
import { compactMentionLabel, normalizeMentionLabel, toMentionToken } from './mentions';

/** Characters the body-text parser accepts after '@'. */
const TOKEN_PATTERN = /^[a-zA-Z0-9_.]*$/;

/**
 * Keys that move the caret without editing the text, so the mention under it
 * has to be read again. Deliberately excludes the keys the suggestion list
 * consumes (arrows up/down, Enter, Tab, Escape): re-reading after those would
 * undo the choice the key just made.
 */
const CARET_MOVE_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'Home', 'End']);

export const isCaretMoveKey = (key: string): boolean => CARET_MOVE_KEYS.has(key);

export interface MentionParticipant {
  userGuid?: string | null;
  /** Name as returned by the API; frequently a full name rather than a handle. */
  username?: string | null;
  firstName?: string | null;
  avatar?: string | null;
}

export interface MentionSuggestion {
  userGuid: string;
  /** Primary label in the list: first name, or the full name when that is all we have. */
  displayName: string;
  avatar?: string;
  /**
   * The text written into the body after '@' and shown as the secondary label.
   * Usually the first name; when two participants share a first name the
   * compacted full name is used instead so the mention still resolves.
   */
  token: string;
  /** Full name, kept for search only — never rendered. */
  fullName?: string;
}

/** An in-progress `@…` at the caret. */
export interface MentionQuery {
  /** Index of the '@' within the input value. */
  start: number;
  /** Text typed between the '@' and the caret. */
  query: string;
}

/**
 * Reads the mention being typed at the caret, or null when the caret is not
 * inside one. An '@' only opens a mention at the start of a word, and the
 * label ends at the first character the parser would not accept.
 */
export function readMentionQuery(
  value: string,
  caret: number | null | undefined,
): MentionQuery | null {
  if (value == null || typeof value !== 'string' || value.length === 0) return null;
  if (caret == null || caret < 0 || caret > value.length) return null;

  const upToCaret = value.slice(0, caret);
  const start = upToCaret.lastIndexOf('@');
  if (start < 0) return null;

  const preceding = start > 0 ? upToCaret[start - 1] : '';
  if (preceding && !/\s/.test(preceding)) return null;

  const query = upToCaret.slice(start + 1);
  if (!TOKEN_PATTERN.test(query)) return null;

  return { start, query };
}

/**
 * Turns thread participants into suggestions, keeping the order given (which
 * carries the priority) and dropping duplicates, the reader, and anyone with
 * no identity to navigate to.
 */
export function buildMentionSuggestions(
  participants: readonly (MentionParticipant | null | undefined)[],
  excludeUserGuid?: string | null,
): MentionSuggestion[] {
  const excluded = normalizeMentionLabel(excludeUserGuid ?? '');
  const unique = new Map<string, {
    userGuid: string;
    displayName: string;
    fullName: string;
    avatar?: string;
  }>();

  for (const participant of participants) {
    const userGuid = participant?.userGuid?.trim();
    if (!userGuid) continue;

    const key = userGuid.toLowerCase();
    if (!key || key === excluded || unique.has(key)) continue;

    const fullName = participant?.username?.trim() ?? '';
    const displayName = participant?.firstName?.trim() || getFirstName(fullName) || fullName;
    if (!displayName) continue;

    unique.set(key, {
      userGuid,
      displayName,
      fullName,
      avatar: participant?.avatar?.trim() || undefined,
    });
  }

  // First pass: compute the first-name token for each person.
  const entries = [...unique.values()];
  const tokenCounts = new Map<string, number>();
  for (const entry of entries) {
    const firstNameToken = toMentionToken(entry.displayName);
    const key = firstNameToken.toLowerCase();
    if (key) tokenCounts.set(key, (tokenCounts.get(key) ?? 0) + 1);
  }

  const suggestions: MentionSuggestion[] = [];
  for (const entry of entries) {
    const firstNameToken = toMentionToken(entry.displayName);
    // When two people share a first name, the bare first name is ambiguous in
    // the mention index, so the compacted full name is written instead — it
    // still resolves to exactly one person.
    const isAmbiguous = firstNameToken && (tokenCounts.get(firstNameToken.toLowerCase()) ?? 0) > 1;
    const token = isAmbiguous
      ? toMentionToken(entry.fullName) || firstNameToken
      : firstNameToken || toMentionToken(entry.fullName);
    if (!token) continue;

    suggestions.push({
      userGuid: entry.userGuid,
      displayName: entry.displayName,
      avatar: entry.avatar,
      token,
      fullName: entry.fullName || undefined,
    });
  }

  return suggestions;
}

/** Filters as the user types: prefix matches first, then anything containing the query. */
export function filterMentionSuggestions(
  suggestions: readonly MentionSuggestion[],
  query: string,
): MentionSuggestion[] {
  const needle = normalizeMentionLabel(query);
  if (!needle) return [...suggestions];

  const startsWith: MentionSuggestion[] = [];
  const contains: MentionSuggestion[] = [];

  for (const suggestion of suggestions) {
    const haystacks = [suggestion.token, suggestion.displayName, suggestion.fullName]
      .map(value => normalizeMentionLabel(compactMentionLabel(value)));

    if (haystacks.some(value => value.startsWith(needle))) startsWith.push(suggestion);
    else if (haystacks.some(value => value.includes(needle))) contains.push(suggestion);
  }

  return [...startsWith, ...contains];
}

/** Replaces the typed `@…` with the chosen mention and reports the new caret. */
export function applyMentionSelection(
  value: string,
  mention: MentionQuery,
  token: string,
): { value: string; caret: number } {
  const before = value.slice(0, mention.start);
  const rest = value.slice(mention.start + 1 + mention.query.length);
  const insertion = `@${token} `;
  // The inserted trailing space stands in for one the text already had.
  const after = rest.startsWith(' ') ? rest.slice(1) : rest;

  return { value: `${before}${insertion}${after}`, caret: before.length + insertion.length };
}
